import { FC } from 'react';

import { useFormik } from 'formik';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { PartnerForm, validationSchema } from './form';

import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { Select } from 'v2/components/select/Select';
import { Switch } from 'v2/components/switch/Switch';
import { PromotionStatus, ProviderName } from 'v2/constants';
import { enumToSelect } from 'v2/utils';

interface PartnerFormDialogProps {
  title: string;
  isOpen: boolean;
  onClose: VoidFunction;
  initialValues: PartnerForm;
  isSubmitting: boolean;
  onSubmit: (values: PartnerForm, resetForm: VoidFunction) => void;
}

export const PartnerFormDialog: FC<PartnerFormDialogProps> = ({
  title,
  isOpen,
  onClose,
  initialValues,
  isSubmitting,
  onSubmit,
}) => {
  const formik = useFormik<PartnerForm>({
    initialValues,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { resetForm }) => {
      onSubmit(values, resetForm);
    },
    enableReinitialize: true,
  });

  const handleSelectChange = (field: keyof PartnerForm) => (v: unknown) => {
    formik.setFieldValue(field, v);
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent className="form">
          <TextField
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Name"
            required
          />

          <TextField
            name="shortDescription"
            value={formik.values.shortDescription}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Description"
            rows={3}
            multiline
            required
          />

          <TextField
            name="link"
            value={formik.values.link}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Link"
            required
          />

          <div className="form-group">
            <Select
              value={formik.values.providerName}
              options={enumToSelect(ProviderName)}
              label="Provider"
              onChange={handleSelectChange('providerName')}
              required
            />

            <Select
              value={formik.values.promotionStatus}
              options={enumToSelect(PromotionStatus)}
              label="Promotion Status"
              onChange={handleSelectChange('promotionStatus')}
            />
          </div>

          <FileSelector
            value={formik.values.iconId}
            label="Partner Icon"
            onSelect={handleSelectChange('iconId')}
            error={formik.errors.iconId}
            required
          />

          <Switch name="isActive" checked={formik.values.isActive} onChange={formik.handleChange} label="Active" />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>

          <Button type="submit" variant="contained" loading={isSubmitting}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
